// @flow
import type { DateObject } from '@cmsgov/ds-healthcare-gov';
import moment from 'moment';

export const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * @param {String} dateString - moment-parseable date string
 * @param {String} dateFormat - string describing moment formatting to use
 * @returns {String} dateString formatted according to dateFormat
 */
export function formatDate(dateString: string, dateFormat: string = DATE_FORMAT): string {
  return moment(dateString, DATE_FORMAT).format(dateFormat);
}

/**
 * @param {Object} date object
 * @returns {String} dateString - Date string used in validations like "YYYY-MM-DD"
 */
export const INVALID_DATE = 'Invalid date';
export function getDateString({ day = '', month = '', year = '' }: DateObject): ?string {
  let dateString = null;
  if (day === '' || month === '' || year === '') {
    if (day !== '' || month !== '' || year !== '') {
      // It's only partially filled, so invalid
      // Shouldn't return null or undefined because we need to distinguish invalid dates from unanswered dates
      dateString = INVALID_DATE;
    }
  } else {
    // It's filled in, so it may or may not be valid
    const date = moment({ month: parseInt(month) - 1, day, year }); // Months are 0 indexed, so we need to subtract 1 from what the user enters
    dateString = date.format(DATE_FORMAT);
  }
  return dateString;
}

/**
 * @param {String} dateString "YYYY-MM-DD"
 * @returns {Object}
 */
export function parseDateString(dateString: ?string): DateObject {
  let year = '';
  let month = '';
  let day = '';
  if (dateString && dateString.length > 0) {
    [year, month, day] = dateString.split('-');
    year = parseInt(year, 10);
    month = parseInt(month, 10);
    day = parseInt(day, 10);
  }
  return {
    year: !isNaN(year) ? year.toString() : '',
    month: !isNaN(month) ? month.toString() : '',
    day: !isNaN(day) ? day.toString() : '',
  };
}

/**
 * @param {Object} date object
 * @returns {Number} number of days between two dates, inclusively
 */
export function getDaysBetween(startDate: DateObject, endDate: DateObject): number {
  const start = moment(getDateString(startDate));
  const end = moment(getDateString(endDate));
  // add one because for calculation purposes, 2/2/2020 and 2/2/2020 should be 1 day (inclusive counting, basically),
  // but moment will take that to be 0 days.
  return end.diff(start, 'days') + 1;
}

/**
 * @param {Object} date object
 * @returns {Number} number of months between two dates, expressed as floating point number if need be
 */
// moment doesn't do fractional months like we want, so this is a custom function to do that
export function getMonthsBetween(startDate: DateObject, endDate: DateObject): number {
  const start = moment(getDateString(startDate));
  const end = moment(getDateString(endDate));
  // Moment zero-indexes months, so add one. Subtracting one from the difference then gives us
  // the actual amount of whole months that don't need any additional calculations. As an example,
  // 4-15-20 to 7-15-20. April and July both are fractions of months, but May and June are counted
  // in their entirety. 7 - 4 - 1 = 2, for those 2 whole months.
  let wholeMonths = end.month() + 1 - (start.month() + 1) - 1;
  // dates are in same month, so do different math
  if (wholeMonths === -1) {
    // if the start is first of the month, we know the remaining calculation is just endDate/total days in month
    if (start.date() === 1) return end.date() / end.daysInMonth();
    // otherwise, we need the actual difference between start and end dates, but inclusive of the start date,
    // so subtract one from it.
    return (end.date() - (start.date() - 1)) / start.daysInMonth();
  }
  let endMonthPortion = end.date() / end.daysInMonth();
  // we want to be inclusive of the start date, so subtract one from it.
  const startMonthPortion = (start.daysInMonth() - (start.date() - 1)) / start.daysInMonth();
  return wholeMonths + endMonthPortion + startMonthPortion;
}

/**
 * @param {Object} year string
 * @returns {Number} number of days in year
 */
export function getDaysInYear(year: string) {
  const isLeapYear = moment(year).isLeapYear();
  const numDays = isLeapYear ? 366 : 365;
  return numDays;
}
